import React, {useState} from 'react';
import {Box, Typography, Collapse, IconButton} from '@mui/material';
import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import PayuForm from './PayuForm';
import {Image} from '@jobseeker/lotus/components/Image';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {useRouter} from 'next/router';
import {PaidProducts} from '@jobseeker/feature-payment/constant';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const ResumeBuilder = () => {
  const theme = useWrappedTheme();

  const [expanded, setExpanded] = useState([]);
  const interceptor: any = useInterceptor();
  const {
    proMembership: {ResumeBuilderPoints},
  } = interceptor;

  const toggleExpand = (index: any) => {
    setExpanded((prev: any) =>
      prev.includes(index)
        ? prev.filter((i: any) => i !== index)
        : [...prev, index]
    );
  };

  if (!ResumeBuilderPoints || !ResumeBuilderPoints.length) {
    return <Typography>Loading...</Typography>;
  }

  const firstPoint = ResumeBuilderPoints[0];
  const remainingPoints = ResumeBuilderPoints.slice(1);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '700px',
        padding: {
          xs: '0 18px 0 19px',
          md: '90px 24px 0 30px',
          lg: '130px 31.3px 69px 25px',
        },
        display: 'flex',
        backgroundColor: 'primary.contrastText',
        flexDirection: {
          xs: 'column',
          lg: 'row',
        },
      }}
    >
      <Box
        sx={{
          height: {md: 320, lg: 380},
          width: 521,
          margin: {xs: '0 auto 41px', md: 'auto', lg: '15px 58px auto 39px'},
          '@media (max-width:1024px) and (min-width:1024px)': {
            '& img': {
              width: 360,
              height: 260,
            },
          },
          '@media (max-width:360px)': {
            '& img': {
              width: 340,
              height: 250,
              marginTop: 5,
            },
          },
          '@media (max-width:768px) and (min-width:768px)': {
            '& img': {
              width: 340,
              height: 300,
              marginLeft: 16,
              marginBottom: 5,
            },
          },
          '@media (max-width:1027px) and (min-width:425px)': {
            '& img': {
              width: 340,
              height: 261,
              margin: 'auto 41px',
            },
          },
          '@media (min-width:1280px)': {
            '& img': {
              width: 520,
              height: 378,
              marginBottom: 5,
            },
          },
        }}
      >
        <Image
          src="https://static.iimjobs.com/static/images/resume-builder-illustration.png"
          alt="Resume Builder"
          width={340}
          height={380}
        />
      </Box>
      <Box
        sx={{
          width: {xs: '100%', md: 'calc(100% - 617px)'},
          '@media (max-width:1024px) and (min-width:1024px)': {
            width: '100%',
          },
          '@media (max-width:375px) and (min-width:375px)': {
            marginTop: 20,
          },
        }}
      >
        <Box
          sx={{
            width: 'max-content',
            height: 34,
            padding: '10px 11px',
            borderRadius: 1,
            backgroundColor: 'border.deepNavy',
            color: 'primary.contrastText',
            marginBottom: '14px',
            fontSize: theme.typography.pxToRem(12),
          }}
        >
          RESUME BUILDER
        </Box>
        <Box sx={{marginBottom: '24px'}}>
          <Typography
            sx={{
              fontSize: theme.typography.pxToRem(30),
              fontWeight: 'bold',
              color: 'border.deepNavy',
              marginBottom: 2,
            }}
          >
            {firstPoint.heading}
          </Typography>
          {firstPoint.description.map((desc, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'start',
                fontSize: theme.typography.pxToRem(15),
                marginTop: 3,
              }}
            >
              <Image
                src="https://job-static.iimjobs.com/V2/static/media/iimjobsgreentick.7e6ffbb0.svg"
                alt="tick"
                style={{marginRight: '8px', marginTop: '5px'}}
                width={14}
                height={14}
              />
              {desc}
            </Box>
          ))}
          {firstPoint.button && <ProButton />}
        </Box>

        {remainingPoints.map((point, index) => (
          <Box key={index} sx={{marginBottom: 3, marginTop: 5}}>
            <Box
              sx={{display: 'flex', cursor: 'pointer'}}
              onClick={() => toggleExpand(index)}
            >
              <Typography
                sx={{
                  width: '95%',
                  fontSize: expanded.includes(index)
                    ? theme.typography.pxToRem(30)
                    : theme.typography.pxToRem(16),
                  fontWeight: expanded.includes(index) ? 'bold' : 'normal',
                  color: expanded.includes(index)
                    ? 'border.deepNavy'
                    : 'inherit',
                  '@media (max-width:1024px) and (min-width:375px)': {
                    width: '100%',
                  },
                }}
              >
                {point.heading}
              </Typography>
              <IconButton
                sx={{
                  width: 16,
                  height: 16,
                  '&:hover': {
                    backgroundColor: '#fff',
                  },
                }}
              >
                {expanded.includes(index) ? '-' : '+'}
              </IconButton>
            </Box>

            <Collapse in={expanded.includes(index)} timeout="auto">
              {point.description.map((desc, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: theme.typography.pxToRem(17),
                    marginTop: 3,
                  }}
                >
                  <Image
                    src="https://job-static.iimjobs.com/V2/static/media/iimjobsgreentick.7e6ffbb0.svg"
                    alt="tick"
                    style={{marginRight: '8px'}}
                    width={14}
                    height={14}
                  />
                  {desc}
                </Box>
              ))}
              {point.button && <ProButton />}
            </Collapse>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ProButton = () => {
  const router = useRouter();
  const isRetry = router.query.retryresumebuilder === 'true';
  const theme = useWrappedTheme();

  return (
    <Box sx={{marginTop: 4.1}}>
      <PayuForm
        key={'pro_membership_plan_button'}
        title={'Buy Now @1999 for 6 months'}
        refCode="0"
        tracking={{action: 'jsSCbanner', category: 'UpgradeToPro'}}
        sx={{
          height: 60,
          width: 320,
          maxWidth: '100%',
          padding: '18px 27px',
          borderRadius: '8px',
          background: theme.palette.primary.main,
          boxShadow: isAppIimjobs
            ? '0 12px 25px 0 rgba(20, 144, 117, 0.33)'
            : ' 0 12px 25px 0 rgba(233,99,12,.33)',
          fontSize: '18px',
          fontWeight: '700',
          color: 'primary.contrastText',
          cursor: 'pointer',
        }}
        product={PaidProducts.RESUME_BUILDER}
        autoTrigger={isRetry}
      />
    </Box>
  );
};

export default ResumeBuilder;
